import { useMemo } from 'react';
import { BaseChoroplethMap } from '../../../components/Charts';
import { useMergedProvincesChoroplethQuery } from '../hooks/useProvincesQuery';
import { geoJsonApi, type GeoJSON } from '../../../services';
import { provinceBoundaryApi } from '../../../services/provinceBoundaryApi';
import { isProvinceBoundaryApiEnabled } from '../../../config/featureFlags';

const MERGED_PROVINCE_FIELDS = ['tenTinh', 'tinh', 'name'] as const;

interface ProvincesMergedChoroplethProps {
	height?: number;
	fullHeight?: boolean;
}

/**
 * Reads current boundaries from the province-lineage API; on any failure (network, validation,
 * empty response) falls back to the static merged file so the map never renders blank. See
 * `plans/260905-1706-province-code-lineage/phase-06-consumer-integration.md`.
 *
 * `ban=hien-hanh` falls back to the 63-code BASE set when no đợt has been APPLIED yet
 * (`boundary-query-service.ts:getHienHanhRows` — correct by design: "hiện hành" with zero
 * applied batches equals "gốc"). But THIS page renders data keyed by the POST-merge code set
 * (`useMergedProvincesChoroplethQuery`), so a same-size response as the base set means no real
 * merge has happened yet and would silently paint the wrong map (mostly gray, no warning) — treat
 * it the same as a failure and fall back to the static merged file.
 */
async function loadFromApiWithStaticFallback(): Promise<GeoJSON> {
	try {
		const [geoJson, baseGeoJson] = await Promise.all([provinceBoundaryApi.getCurrent(), provinceBoundaryApi.getBase()]);
		if (!geoJson.features.length) {
			throw new Error('provinceBoundaryApi.getCurrent() trả về FeatureCollection rỗng');
		}
		if (geoJson.features.length === baseGeoJson.features.length) {
			throw new Error(
				`provinceBoundaryApi.getCurrent() trả về ${geoJson.features.length} mã -- bằng đúng tập mã gốc (${baseGeoJson.features.length}), nghĩa là chưa có đợt sáp nhập nào được APPLIED`,
			);
		}
		return geoJson;
	} catch (err) {
		console.warn(
			'Không tải được ranh giới tỉnh sau sáp nhập từ API (VITE_PROVINCE_BOUNDARY_API), dùng lại file tĩnh provinces.merged.geojson:',
			err,
		);
		return geoJsonApi.getMergedProvinces();
	}
}

const loadFromStaticFile = (): Promise<GeoJSON> => geoJsonApi.getMergedProvinces();

export function ProvincesMergedChoropleth({ height = 800, fullHeight = false }: ProvincesMergedChoroplethProps) {
	const flagEnabled = isProvinceBoundaryApiEnabled();

	// `loadGeoJson` sits in `BaseChoroplethMap`'s effect dependency array — a fresh arrow
	// function on every render would re-trigger `registerMap` in an infinite loop. `useMemo`
	// keyed on the flag (which doesn't change across renders) keeps identity stable.
	const loadGeoJson = useMemo(
		() => (flagEnabled ? loadFromApiWithStaticFallback : loadFromStaticFile),
		[flagEnabled],
	);

	return (
		<BaseChoroplethMap
			height={height}
			fullHeight={fullHeight}
			mapKey="vietnam-merged"
			titlePrefix="Bản đồ Phân bố Sinh viên sau Sáp nhập"
			infoTooltip="Dữ liệu đã được sáp nhập theo mã tỉnh mới"
			errorMessage="Có lỗi xảy ra khi tải dữ liệu bản đồ sau sáp nhập"
			geoJsonErrorMessage="Không thể tải dữ liệu bản đồ sau sáp nhập"
			loadGeoJson={loadGeoJson}
			useChoroplethQuery={useMergedProvincesChoroplethQuery}
			provinceNameFields={MERGED_PROVINCE_FIELDS}
		/>
	);
}
